import { useCallback, useRef, useState } from "react";
import { IconKoperta, IconStrzalkaDol, IconTelefon, IconWyloguj } from "../icons/index.js";
import { inicjaly } from "../ui/inicjaly.js";
import { ThemeToggle } from "./ThemeToggle.js";
import { useOutsideDismiss } from "./useOutsideDismiss.js";

/**
 * L-7 (klaster konta przy prawej), L-13 (menu profilu), B-8 (awatar), L-16 (toggle).
 *
 * Zawartosc menu z gory na dol wg L-13: naglowek tozsamosci, akcje zalezne od progu,
 * stala sekcja "Pomoc / kontakt".
 *
 * Nazwa uzytkownika pojawia sie na desktopie DWA razy: na pasku i w naglowku menu.
 * To nie jest dublowanie, ktorego zabrania L-12 - ten zakaz dotyczy AKCJI (motyw,
 * wyloguj), nie tozsamosci. Tak samo robi gotowiec.
 */

interface Props {
  username: string;
  onSignOut: () => void | Promise<void>;
}

export function ProfileMenu({ username, onSignOut }: Props): React.JSX.Element {
  const [otwarte, setOtwarte] = useState(false);
  const kontener = useRef<HTMLDivElement>(null);
  const wyzwalacz = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const zamknij = useCallback((zwrocFokus: boolean) => {
    setOtwarte(false);
    if (zwrocFokus) wyzwalacz.current?.focus();
  }, []);

  useOutsideDismiss(otwarte, [kontener], zamknij);

  const przelacz = (): void => {
    const nastepne = !otwarte;
    setOtwarte(nastepne);
    if (nastepne) {
      // A-4: po otwarciu fokus ladzie na pierwszej WIDOCZNEJ pozycji. Widocznej,
      // bo czesc pozycji jest ukryta media query zaleznie od progu.
      requestAnimationFrame(() => {
        const pozycje = panel.current?.querySelectorAll<HTMLElement>('[role="menuitem"], .acct-menu__contact a');
        for (const el of pozycje ?? []) {
          if (el.offsetParent !== null) {
            el.focus();
            return;
          }
        }
      });
    }
  };

  const skrot = inicjaly(username);

  return (
    <div className="acct" ref={kontener}>
      <button
        type="button"
        ref={wyzwalacz}
        className="hbtn acct-trigger"
        aria-haspopup="menu"
        aria-expanded={otwarte}
        aria-label={`Konto: ${username}`}
        onClick={przelacz}
      >
        <span className="acct-initials" aria-hidden="true">
          {skrot}
        </span>
        <span className="acct-name">{username}</span>
        <IconStrzalkaDol className="acct-chev" size={16} />
      </button>

      <div
        className={`acct-menu${otwarte ? " is-open" : ""}`}
        role="menu"
        aria-label="Menu konta"
        ref={panel}
        // Ukryte przed czytnikiem, dopoki zamkniete - samo pointer-events nie wystarcza.
        aria-hidden={!otwarte}
      >
        <div className="acct-menu__head">
          <span className="acct-menu__avatar" aria-hidden="true">
            {skrot}
          </span>
          <div>
            <p className="acct-menu__name">{username}</p>
          </div>
        </div>
        <hr className="acct-menu__divider" />

        {/*
          L-13: klik w motyw NIE zamyka menu. Dlatego ThemeToggle nie wola tu zamknij() -
          klient ma zobaczyc efekt przelaczenia bez tracenia kontekstu.
          L-12: ta pozycja jest widoczna wylacznie na telefonie; od tabletu motyw
          stoi na pasku. Para jest komplementarna, wiec akcja wystepuje dokladnie raz.
        */}
        <ThemeToggle wariant="menu" />

        <button
          type="button"
          className="acct-menu__item menu-logout"
          role="menuitem"
          onClick={() => {
            zamknij(false);
            void onSignOut();
          }}
        >
          <IconWyloguj />
          Wyloguj
        </button>

        <hr className="acct-menu__divider acct-menu__divider--phone" />

        {/* L-13: sekcja kontaktu jest stala, na kazdym progu. */}
        <div className="acct-menu__contact">
          <p className="acct-menu__contact-label">Pomoc i kontakt</p>
          <a href="tel:+48884514514" onClick={() => zamknij(false)}>
            <IconTelefon />
            +48 884 514 514
          </a>
          <a href="mailto:hi@urmate.ai" onClick={() => zamknij(false)}>
            <IconKoperta />
            hi@urmate.ai
          </a>
        </div>
      </div>
    </div>
  );
}
